'use client';

import { useState, useEffect } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import apiClient from '@/lib/api';
import { Article } from '@/types';

export default function CategoryPage() {
  const { name } = useParams();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategoryArticles = async () => {
    try {
      const results = await apiClient.getArticles({ category: decodeURIComponent(name as string) });
      setArticles(results);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching category articles:', err);
      setError('Error loading category articles');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (name) {
      fetchCategoryArticles();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const categoryName = name ? decodeURIComponent(name as string) : '';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading articles...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Link 
            href="/"
            className="grok-btn-primary px-6 py-3"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Category Header */}
      <div className="mb-8">
        <nav className="text-sm mb-4">
          <Link href="/" className="text-white hover:underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-400">Category</span>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-white">{categoryName}</span>
        </nav>
        
        <h1 className="text-4xl font-bold text-white mb-4">
          {categoryName} Articles
        </h1>
        <p className="text-lg text-gray-300">
          {articles.length} article{articles.length !== 1 ? 's' : ''} in this category
        </p>
      </div>

      {/* Articles Grid */}
      {articles.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📂</div>
          <h2 className="text-2xl font-semibold text-white mb-4">No articles found</h2>
          <p className="text-gray-300 mb-8">There are no articles in the {categoryName} category yet.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link 
              href="/contribute"
              className="grok-btn-primary px-6 py-3"
            >
              Contribute an Article
            </Link>
            <Link 
              href="/"
              className="grok-btn-secondary px-6 py-3"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.map((article) => (
            <Link 
              key={article._id} 
              href={`/wiki/${encodeURIComponent(article.title)}`}
              className="block grok-card overflow-hidden"
            >
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">{article.title}</h3>
                <p className="text-gray-300 text-sm mb-4 line-clamp-3">{article.summary}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(article.categories || []).map((category) => (
                    <span 
                      key={typeof category === 'string' ? category : category._id} 
                      className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                        (typeof category === 'string' ? category : category.name) === categoryName 
                          ? 'bg-white text-black' 
                          : 'bg-gray-700 text-gray-300'
                      }`}
                    >
                      {typeof category === 'string' ? category : category.name}
                    </span>
                  ))}
                </div>
                <div className="flex justify-between items-center text-sm text-gray-400">
                  <span>{article.viewsCount} views</span>
                  {article.qualityScore && (
                    <span className="flex items-center">
                      ⭐ {article.qualityScore}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
